/**
 * StartFunnelWidget — «Воронка старта» (вкладка раздела «Прогрессия»)
 * ===================================================================
 *
 * Четыре блока на одной вкладке:
 *  1) Воронка шагов: [установка] → регистрация → уровень 1 → уровень 3 →
 *     туториал → уровень 5 → 10 → 20 → 50, с конверсиями «из предыдущего» и
 *     «из когорты». Порядок шагов задан на сервере (START_FUNNEL_STEPS) и
 *     ИЗМЕРЕН: туториал AvZ тянется примерно до 3-4 уровня кампании, поэтому
 *     он стоит между уровнями 3 и 5, а не сразу после регистрации.
 *  2) Кумулятивная кривая отвала по уровням до 150 (доля когорты с
 *     максимальным пройденным уровнем >= N).
 *  3) Время до шага: медиана и среднее дня жизни + доля дошедших.
 *  4) Сравнение до 3 когорт (по частям окна или по версиям установки),
 *     дельты в процентных пунктах.
 *
 * ВАЖНО ПРО ОСЬ: вкладка КОГОРТНАЯ — диапазон дат дашборда трактуется как
 * дата регистрации (acc_reg_ts), а не как календарный период события. Плюс
 * из когорты исключаются дни, не прожившие полный горизонт наблюдения; обе
 * оговорки печатаются в подзаголовке, а не подразумеваются.
 */

import React, { useMemo, useState } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { MetricsService } from '../../services/metricsService';
import { progression as progressionSpecs } from '@ggstat/chart-specs';
import { withDefaultDateRange } from '../../utils/defaultDateRange';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/table';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';

/** Горизонты наблюдения ('all' сервер трактует как «всё время»). */
// Горизонты, режимы сравнения, цвета шагов/отвала/когорт — общие с MCP (shared/chart-specs)
const {
  horizons: HORIZONS,
  defaultHorizon: DEFAULT_HORIZON,
  compareModes: COMPARE_MODES,
  stepColors: STEP_COLORS,
  dropoffSeries: [DROPOFF_SERIES],
  comparePalette: PALETTE
} = progressionSpecs.startFunnel;

const fmtInt = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`);
const fmtDays = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('ru-RU'));

/** Горизонт из строки контрола в аргумент сервиса (число либо null). */
function horizonValue(horizon) {
  return horizon === 'all' ? null : Number(horizon);
}

/** Сдвиг ISO-даты на N дней. */
function shiftDays(iso, days) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Подзаголовок: когортная семантика + фактическое окно + сколько дней
 * исключено. Молча укороченный период читается как баг данных, поэтому обе
 * обрезки называются вслух вместе с причиной.
 */
function funnelSubtitle(dateRange) {
  if (!dateRange?.start) {
    return 'Когорта по дате регистрации (acc_reg_ts), а не календарный период события';
  }
  const notes = [
    `когорта (регистрация): ${dateRange.start} — ${dateRange.end}`,
    `горизонт наблюдения: ${dateRange.horizonDays ?? 'всё время'}`
  ];
  if (dateRange.excludedDays > 0) {
    notes.push(
      `исключено ${dateRange.excludedDays} дн. когорты: они не прожили полный горизонт ` +
        `(последние завершённые сутки — ${dateRange.lastCompleted})`
    );
  }
  if (dateRange.cappedStart) {
    notes.push(`начало окна — ${dateRange.start}: раньше этой даты данных нет`);
  }
  if (dateRange.empty) {
    notes.push('в окне не осталось когорт с полным горизонтом');
  }
  return `${notes.join(' · ')} · вкладка когортная, в отличие от календарных дашбордов`;
}

/** Горизонтальный бар одного шага воронки. */
function FunnelBar({ step, max, color }) {
  const width = max > 0 ? Math.max(1, Math.round((step.devices / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-44 shrink-0 truncate" title={step.label}>
        {step.label}
        {!step.inChain && <span className="text-muted-foreground"> (вне цепочки)</span>}
      </span>
      <div className="flex-1 bg-muted/40 rounded h-5 overflow-hidden">
        <div
          className="h-5 rounded flex items-center justify-end pr-2 text-[11px] text-white"
          style={{ width: `${width}%`, backgroundColor: color, minWidth: 44 }}
        >
          {fmtInt(step.devices)}
        </div>
      </div>
      <span className="w-16 shrink-0 text-right text-muted-foreground">
        {fmtPct(step.conversionFromPrev)}
      </span>
      <span className="w-16 shrink-0 text-right text-muted-foreground">
        {fmtPct(step.conversionFromCohort)}
      </span>
    </div>
  );
}

export function StartFunnelWidget() {
  // Локальные контролы вкладки: горизонт наблюдения и режим сравнения.
  // Платформа и диапазон дат — из ГЛОБАЛЬНЫХ фильтров дашборда.
  const [horizon, setHorizon] = useState(DEFAULT_HORIZON);
  const [compareMode, setCompareMode] = useState('none');
  const [versions, setVersions] = useState(['', '', '']);

  // Блоки 1-3 идут одним запросом виджета: у них общий горизонт и общее окно,
  // поэтому и грузить их врозь незачем.
  const widgetData = useWidgetData(
    async (filters, options) => {
      const scoped = withDefaultDateRange(filters);
      const h = horizonValue(horizon);
      const [funnel, dropoff, time] = await Promise.all([
        MetricsService.getStartFunnel(scoped, h, undefined, options),
        MetricsService.getStartFunnelDropoff(scoped, h, undefined, options),
        MetricsService.getStartFunnelTimeToStep(scoped, h, undefined, options)
      ]);
      return { funnel, dropoff, time };
    },
    [horizon]
  );

  const widgetProps = useBaseWidget(widgetData);
  const { data } = widgetProps;

  const funnel = data?.funnel;
  const dropoff = data?.dropoff;
  const time = data?.time;
  const dateRange = funnel?.dateRange;

  // Список версий установки для селекторов сравнения — только когда режим
  // сравнения по версиям выбран (иначе это лишний скан на каждое открытие).
  const versionsData = useWidgetData(
    async (filters, options) => {
      if (compareMode !== 'versions') return { versions: [] };
      return MetricsService.getStartFunnelClientVersions(
        withDefaultDateRange(filters),
        horizonValue(horizon),
        options
      );
    },
    [compareMode, horizon]
  );
  const versionOptions = versionsData.data?.versions || [];

  // Когорты сравнения: либо три равные части окна, либо до трёх версий.
  const compareCohorts = useMemo(() => {
    if (compareMode === 'none' || !dateRange?.start) return [];

    if (compareMode === 'parts') {
      const start = dateRange.start;
      const end = dateRange.end;
      const total =
        Math.round(
          (new Date(`${end}T00:00:00Z`).getTime() - new Date(`${start}T00:00:00Z`).getTime()) /
            86400000
        ) + 1;
      // Меньше трёх дней в окне — делить нечего, сравнение не строим.
      if (total < 3) return [];
      const chunk = Math.floor(total / 3);
      return [0, 1, 2].map((i) => {
        const from = shiftDays(start, i * chunk);
        const to = i === 2 ? end : shiftDays(start, (i + 1) * chunk - 1);
        return { label: `${from}…${to}`, startDate: from, endDate: to };
      });
    }

    // Дедуп: одну и ту же версию, выбранную в двух селекторах, считать дважды
    // незачем — и React получил бы два одинаковых key на строку сравнения.
    return Array.from(new Set(versions.filter(Boolean))).map((clientVerI) => ({
      label: clientVerI,
      clientVerI
    }));
  }, [compareMode, dateRange?.start, dateRange?.end, versions]);

  const compareKey = JSON.stringify(compareCohorts);
  const compareData = useWidgetData(
    async (filters, options) => {
      if (compareCohorts.length === 0) return { cohorts: [], deltas: [] };
      return MetricsService.getStartFunnelCompare(
        withDefaultDateRange(filters),
        compareCohorts,
        horizonValue(horizon),
        options
      );
    },
    [compareKey, horizon]
  );
  const compare = compareData.data;

  // С фильтром версии сервер не отдаёт установки (AppsFlyer без версий): шаг
  // «Установка» и покрытие скрываем, вместо них — заглушка.
  const installsBaseAvailable = funnel?.install?.installsBaseAvailable !== false;
  const visibleSteps = useMemo(
    () => (funnel?.steps || []).filter((s) => installsBaseAvailable || s.kind !== 'install'),
    [funnel, installsBaseAvailable]
  );

  const maxDevices = useMemo(
    () => Math.max(1, ...visibleSteps.map((s) => s.devices)),
    [visibleSteps]
  );

  // Элемент, а не вложенный компонент: у нового компонента на каждый рендер
  // своя identity, и React перемонтировал бы ToggleGroup на каждое изменение
  // состояния виджета.
  const controls = (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">Горизонт наблюдения</Label>
      <ToggleGroup
        type="single"
        value={horizon}
        onValueChange={(value) => value && setHorizon(value)}
      >
        {HORIZONS.map((h) => (
          <ToggleGroupItem key={h.value} value={h.value} aria-label={h.label}>
            {h.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );

  return (
    <BaseWidget
      {...widgetProps}
      title="Воронка старта (когорта)"
      subtitle={funnelSubtitle(dateRange)}
      headerAction={controls}
      emptyMessage="Нет данных за выбранную когорту"
      skeletonRows={10}
    >
      <div className="space-y-8">
        {/* --- Блок 1: воронка шагов --- */}
        <section className="space-y-2">
          <div className="flex items-baseline justify-between">
            <p className="text-sm font-medium">
              Шаги воронки · когорта {fmtInt(funnel?.cohortSize)} устройств
            </p>
            <p className="text-xs text-muted-foreground">
              справа: % из предыдущего шага · % из когорты
            </p>
          </div>
          <div className="space-y-1.5">
            {visibleSteps.map((step) => (
              <FunnelBar
                key={step.key}
                step={step}
                max={maxDevices}
                color={step.inChain ? STEP_COLORS.inChain.color : STEP_COLORS.outOfChain.color}
              />
            ))}
          </div>
          {funnel?.install && !installsBaseAvailable && (
            <p className="text-xs text-muted-foreground">
              Шаг «Установка» и покрытие установками: Недоступно с фильтром версии.
            </p>
          )}
          {funnel?.install && installsBaseAvailable && (
            <p className="text-xs text-muted-foreground">
              Установки AppsFlyer: {fmtInt(funnel.install.installDevices)} · сопоставлено с
              когортой {fmtInt(funnel.install.matchedDevices)} — покрытие когорты{' '}
              {fmtPct(funnel.install.matchRateForward)}, установка→регистрация{' '}
              {fmtPct(funnel.install.matchRateReverse)}. Шаг «установка» ВНЕ цепочки
              конверсий: это другая популяция (строки AppsFlyer), и часть разрыва —
              атрибуция, а не отвал игроков.
            </p>
          )}
        </section>

        {/* --- Блок 2: кривая отвала --- */}
        <section className="space-y-2">
          <p className="text-sm font-medium">
            Отвал по уровням кампании (кумулятивно: доля когорты с максимальным уровнем ≥ N)
          </p>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={dropoff?.data || []} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis
                dataKey="level"
                type="number"
                domain={['dataMin', 'dataMax']}
                allowDecimals={false}
                tick={{ fill: 'currentColor' }}
                className="text-xs"
                label={{ value: 'Уровень кампании', position: 'insideBottom', offset: -2 }}
              />
              <YAxis
                tick={{ fill: 'currentColor' }}
                className="text-xs"
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                formatter={(value, name, entry) => [
                  `${Number(value).toFixed(1)}% (${fmtInt(entry?.payload?.devices)} устройств)`,
                  DROPOFF_SERIES.label
                ]}
                labelFormatter={(label) => `Уровень ${label}`}
              />
              <Line
                type="monotone"
                dataKey={DROPOFF_SERIES.key}
                name={DROPOFF_SERIES.label}
                stroke={DROPOFF_SERIES.color}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-xs text-muted-foreground">
            Определение кумулятивное намеренно: 11.8% когорты пользуется level_skip, поэтому
            «впервые прошёл ровно уровень N» немонотонно по уровню и читается как ошибка.
          </p>
        </section>

        {/* --- Блок 3: время до шага --- */}
        <section className="space-y-2">
          <p className="text-sm font-medium">Время до шага (дни жизни)</p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Шаг</TableHead>
                <TableHead className="text-right">Дошло</TableHead>
                <TableHead className="text-right">Доля когорты</TableHead>
                <TableHead className="text-right">Медиана, дн</TableHead>
                <TableHead className="text-right">Среднее, дн</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(time?.data || []).map((row) => (
                <TableRow key={row.key}>
                  <TableCell className="font-medium">{row.label}</TableCell>
                  <TableCell className="text-right">{fmtInt(row.reached)}</TableCell>
                  <TableCell className="text-right">{fmtPct(row.reachShare)}</TableCell>
                  <TableCell className="text-right">{fmtDays(row.medianDays)}</TableCell>
                  <TableCell className="text-right">{fmtDays(row.avgDays)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="text-xs text-muted-foreground">
            Считается только по дошедшим игрокам. «Дошло» здесь — факт первого прохождения
            именно этого уровня, а конверсия блока 1 кумулятивна, поэтому доли отличаются:
            перескочивший уровень через level_skip в блоке 1 засчитан, а времени до уровня
            у него нет.
          </p>
        </section>

        {/* --- Блок 4: сравнение когорт --- */}
        <section className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Сравнение когорт (до 3)</Label>
              <Select value={compareMode} onValueChange={setCompareMode}>
                <SelectTrigger className="w-[220px] h-9 text-xs">
                  <SelectValue placeholder="Сравнение" />
                </SelectTrigger>
                <SelectContent>
                  {COMPARE_MODES.map((mode) => (
                    <SelectItem key={mode.value} value={mode.value}>
                      {mode.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {compareMode === 'versions' &&
              [0, 1, 2].map((i) => (
                <div key={i} className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Версия {i + 1}</Label>
                  <Select
                    value={versions[i] || 'none'}
                    onValueChange={(value) =>
                      setVersions((prev) => {
                        const next = [...prev];
                        next[i] = value === 'none' ? '' : value;
                        return next;
                      })
                    }
                  >
                    <SelectTrigger className="w-[170px] h-9 text-xs">
                      <SelectValue placeholder="client_ver_i" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— не выбрана —</SelectItem>
                      {versionOptions
                        .filter((v) => v.clientVerI === versions[i] || !versions.includes(v.clientVerI))
                        .map((v) => (
                          <SelectItem key={v.clientVerI} value={v.clientVerI}>
                            {v.clientVerI} ({fmtInt(v.devices)})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
          </div>

          {compare?.cohorts?.length > 0 && (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="level"
                    type="number"
                    allowDuplicatedCategory={false}
                    domain={['dataMin', 'dataMax']}
                    allowDecimals={false}
                    tick={{ fill: 'currentColor' }}
                    className="text-xs"
                    label={{ value: 'Уровень кампании', position: 'insideBottom', offset: -2 }}
                  />
                  <YAxis
                    tick={{ fill: 'currentColor' }}
                    className="text-xs"
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip labelFormatter={(label) => `Уровень ${label}`} />
                  <Legend />
                  {compare.cohorts.map((cohort, i) => (
                    <Line
                      key={cohort.label}
                      data={cohort.dropoff}
                      dataKey="share"
                      name={`${cohort.label} (${fmtInt(cohort.cohortSize)})`}
                      stroke={PALETTE[i % PALETTE.length]}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Шаг</TableHead>
                    {compare.cohorts.map((cohort) => (
                      <TableHead key={cohort.label} className="text-right">
                        {cohort.label}
                      </TableHead>
                    ))}
                    {compare.cohorts.slice(1).map((cohort) => (
                      <TableHead key={`d-${cohort.label}`} className="text-right">
                        Δ {cohort.label}, п.п.
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(compare.deltas || []).map((row) => (
                    <TableRow key={row.key}>
                      <TableCell className="font-medium">{row.label}</TableCell>
                      {row.shares.map((share, i) => (
                        <TableCell key={`s-${i}`} className="text-right">
                          {fmtPct(share)}
                        </TableCell>
                      ))}
                      {row.deltasPp.slice(1).map((delta, i) => (
                        <TableCell
                          key={`d-${i}`}
                          className={`text-right ${
                            delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-600' : ''
                          }`}
                        >
                          {delta === null || delta === undefined
                            ? '—'
                            : `${delta > 0 ? '+' : ''}${delta.toFixed(1)}`}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          {compare?.cohorts?.length > 1 && horizon === 'all' && (
            <p className="text-xs text-amber-600">
              Внимание: на горизонте «всё время» когорты разного возраста не сопоставимы —
              у более молодой меньше прожитых дней, и её конверсии заведомо ниже. Для
              сравнения выберите фиксированный горизонт (7/14/30/60): тогда из каждой
              когорты исключаются дни без полного окна наблюдения.
            </p>
          )}

          {compareMode !== 'none' && compare?.cohorts?.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {compareMode === 'versions'
                ? 'Выберите хотя бы одну версию установки.'
                : 'В окне слишком мало дней, чтобы разбить его на три когорты.'}
            </p>
          )}
        </section>
      </div>
    </BaseWidget>
  );
}

export default StartFunnelWidget;
